import {API} from '../../networking/http'
import {queryClient} from '../../utils/react-query'
import {QueryMethod} from '../helpers/types'
import {TerminalData} from '../payments/types'
import {getOpnChargeState, isOpnChargeDegraded, parseIntegrationsStatus} from './helpers'
import {IntegrationsStatus, ProviderHealthState} from './types'

export type GetProviderStatusParams = {venueId?: string | null}
export type GetProviderStatusQuery = QueryMethod<GetProviderStatusParams, GetProviderStatusParams, IntegrationsStatus | null>

const ONE_MINUTE = 1000 * 60
export const PROVIDER_STATUS_TIMEOUT = 3000

export const PROVIDER_STATUS_ENDPOINT = 'integrations/status'

const withTimeout = <T>(promise: Promise<T>, fallback: T, ms: number): Promise<T> =>
  new Promise((resolve) => {
    const timer = setTimeout(() => resolve(fallback), ms)
    promise
      .then(resolve, () => resolve(fallback))
      .finally(() => clearTimeout(timer))
  })

// Fail open: a failed, slow (>3 s) or malformed status read is "unknown", never a gate.
export const fetchIntegrationsStatus = (venueId?: string | null): Promise<IntegrationsStatus | null> =>
  withTimeout(
    API.get<undefined, unknown>(PROVIDER_STATUS_ENDPOINT, {
      params: venueId ? {venueId} : undefined,
      timeout: PROVIDER_STATUS_TIMEOUT,
    }).then((res) => (res.status === 200 ? parseIntegrationsStatus(res.data) : null)),
    null,
    PROVIDER_STATUS_TIMEOUT,
  )

export const getProviderStatusQuery: GetProviderStatusQuery = ({params, ...options}) => ({
  queryKey: ['getProviderStatusQuery', {venueId: params?.venueId || null}],
  staleTime: ONE_MINUTE,
  refetchOnWindowFocus: true,
  // No polling while charges work. While charge is DOWN the pay button is disabled, so no submit
  // re-read happens: re-read once a minute so the gate clears by itself. Observers only exist on a
  // mounted pay step, and react-query pauses intervals in a background tab.
  refetchInterval: (data) => (getOpnChargeState(data) === 'DOWN' ? ONE_MINUTE : false),
  retry: false,
  ...options,
  queryFn: () => fetchIntegrationsStatus(params?.venueId),
})

// The read right before a charge: served from the 60 s cache, otherwise one fresh request.
export const readOpnChargeState = (venueId?: string | null): Promise<ProviderHealthState | null> =>
  queryClient
    .fetchQuery(getProviderStatusQuery({params: {venueId}}))
    .then(getOpnChargeState)
    .catch(() => null)

export const getCachedOpnChargeState = (venueId?: string | null): ProviderHealthState | null =>
  getOpnChargeState(
    queryClient.getQueryData<IntegrationsStatus | null>(['getProviderStatusQuery', {venueId: venueId || null}]),
  )

// DEGRADED only matters to a venue whose terminal is Opn; the terminal is cached by the pay step.
export const isCachedOpnChargeDegraded = (venueId?: string | null): boolean =>
  isOpnChargeDegraded({
    provider: queryClient.getQueryData<TerminalData | null>(['getTerminalQuery', {venueId: venueId || undefined}])?.provider,
    chargeState: getCachedOpnChargeState(venueId),
  })
